import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { applyBrandColor } from '@/lib/theme';
import { ArrowLeft, Upload, Save, Palette, User, Key } from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const Settings = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    primary_color: '#1B4332',
    sender_email: '',
    default_expiry_days: '7',
  });
  const [profileData, setProfileData] = useState({
    email: '',
    full_name: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData(prev => ({ ...prev, email: user.email || '', full_name: user.full_name || '' }));
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data);
      setFormData({
        name: res.data.name || '',
        primary_color: res.data.primary_color || '#1B4332',
        sender_email: res.data.sender_email || '',
        default_expiry_days: String(res.data.default_expiry_days || 7),
      });
      if (res.data.logo_path) {
        loadLogo(res.data.logo_path);
      }
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadLogo = async (path) => {
    try {
      const res = await axios.get(`${API_URL}/files/${path}?auth=${token}`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      setLogoUrl(blobUrl);
    } catch {
      // Logo not available yet
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, default_expiry_days: parseInt(formData.default_expiry_days) || 7 };
      await axios.patch(`${API_URL}/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyBrandColor(formData.primary_color);
      toast.success('Settings updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and GIF images are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be under 2MB');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/settings/logo`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Logo uploaded successfully');
      // Reload to show new logo
      if (res.data.path) {
        loadLogo(res.data.path);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileData.current_password) { toast.error('Enter your current password to confirm changes'); return; }
    if (profileData.new_password && profileData.new_password !== profileData.confirm_password) { toast.error('New passwords do not match'); return; }
    if (profileData.new_password && profileData.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setSavingProfile(true);
    try {
      await api.updateProfile({
        email: profileData.email || undefined,
        full_name: profileData.full_name || undefined,
        current_password: profileData.current_password,
        new_password: profileData.new_password || undefined,
      });
      toast.success('Profile updated successfully');
      setProfileData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }));
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSavingProfile(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <div className="max-w-3xl mx-auto p-8">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="rounded-md mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
        </Button>

        <div className="mb-8">
          <h1
            className="text-2xl font-bold text-neutral-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="settings-title"
          >
            Organisation Settings
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Configure your organisation branding and preferences
          </p>
        </div>

        {/* Logo Section */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <h2
            className="text-lg font-semibold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Organisation Logo
          </h2>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-neutral-300 flex items-center justify-center overflow-hidden bg-neutral-50">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Organisation Logo"
                  className="w-full h-full object-contain"
                  data-testid="logo-preview"
                />
              ) : (
                <Upload className="h-8 w-8 text-neutral-400" />
              )}
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="rounded-md mb-2"
                disabled={uploading}
                data-testid="upload-logo-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-neutral-500">
                JPEG, PNG, WebP, or GIF. Max 2MB.
              </p>
            </div>
          </div>
        </div>

        {/* General Settings */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <h2
            className="text-lg font-semibold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            General Settings
          </h2>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-sm">Organisation Name</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="rounded-md"
                placeholder="Wildnet Technologies"
                data-testid="org-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Primary Brand Color</Label>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) =>
                      setFormData({ ...formData, primary_color: e.target.value })
                    }
                    className="w-12 h-12 rounded-md border border-neutral-200 cursor-pointer"
                    data-testid="color-picker"
                  />
                </div>
                <Input
                  value={formData.primary_color}
                  onChange={(e) =>
                    setFormData({ ...formData, primary_color: e.target.value })
                  }
                  className="rounded-md max-w-[140px] font-mono text-sm"
                  placeholder="#1B4332"
                  data-testid="color-hex-input"
                />
                <div
                  className="h-10 flex-1 rounded-md border border-neutral-200"
                  style={{ backgroundColor: formData.primary_color }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Sender Email Address</Label>
              <Input
                value={formData.sender_email}
                onChange={(e) =>
                  setFormData({ ...formData, sender_email: e.target.value })
                }
                className="rounded-md"
                placeholder="hr@wildnettechnologies.com"
                data-testid="sender-email-input"
              />
              <p className="text-xs text-neutral-500">
                Must be verified in your Resend dashboard. Used for candidate invite
                and notification emails.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Default Link Expiry</Label>
              <Select value={formData.default_expiry_days} onValueChange={(v) => setFormData({ ...formData, default_expiry_days: v })}>
                <SelectTrigger className="rounded-md max-w-[200px]" data-testid="default-expiry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500">
                Default expiry for new assessment invite links. Can be overridden per assessment.
              </p>
            </div>

            <Button
              type="submit"
              className="bg-brand hover:bg-brand-dark text-white rounded-md"
              disabled={saving}
              data-testid="save-settings-button"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h2
            className="text-lg font-semibold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Brand Preview
          </h2>
          <div
            className="p-6 rounded-lg text-white"
            style={{ backgroundColor: formData.primary_color || 'var(--brand)' }}
          >
            <div className="flex items-center gap-4">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="w-10 h-10 object-contain bg-white rounded-md p-1"
                />
              )}
              <div>
                <h3 className="text-lg font-bold">
                  {formData.name || 'Organisation Name'}
                </h3>
                <p className="text-sm opacity-80">Candidate Assessment Portal</p>
              </div>
            </div>
          </div>
        </div>

        {/* Profile / Account Section */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6" data-testid="profile-section">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-brand" />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Account Settings</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-5">Change your login email, name, or password. Enter your current password to confirm any changes.</p>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Email Address</Label>
                <Input value={profileData.email} onChange={(e) => setProfileData({ ...profileData, email: e.target.value })} className="rounded-md" data-testid="profile-email-input" type="email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Full Name</Label>
                <Input value={profileData.full_name} onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })} className="rounded-md" data-testid="profile-name-input" />
              </div>
            </div>
            <hr className="border-neutral-100" />
            <div className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1.5"><Key className="h-3.5 w-3.5" /> Current Password *</Label>
              <PasswordInput value={profileData.current_password} onChange={(e) => setProfileData({ ...profileData, current_password: e.target.value })} className="rounded-md max-w-sm" placeholder="Required to save changes" data-testid="current-password-input" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">New Password</Label>
                <PasswordInput value={profileData.new_password} onChange={(e) => setProfileData({ ...profileData, new_password: e.target.value })} className="rounded-md" placeholder="Leave blank to keep current" data-testid="new-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Confirm New Password</Label>
                <PasswordInput value={profileData.confirm_password} onChange={(e) => setProfileData({ ...profileData, confirm_password: e.target.value })} className="rounded-md" placeholder="Repeat new password" data-testid="confirm-password-input" />
              </div>
            </div>
            <Button type="submit" className="bg-brand hover:bg-brand-dark text-white rounded-md" disabled={savingProfile} data-testid="save-profile-button">
              <Save className="h-4 w-4 mr-2" /> {savingProfile ? 'Saving...' : 'Update Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
